/**
 * Anon queue — localStorage-backed, product-scoped (narcis_funnel plan, Architecture decision 3:
 * "Anon queue lives in localStorage… merged server-side by an action on the first authenticated
 * load after sign-in"). Mirrors `PACK_QUEUE_CAP` from `packQueue.server.ts` for the same "queue
 * full" UX before the merge ever reaches the server — the server guard is still authoritative;
 * this is a client-side mirror so an anon visitor sees "queue full" honestly rather than being
 * told "queued" and then silently losing a slot on merge.
 *
 * Order is preserved (array order = queue order, same as the DB's `position` column), and
 * `mergeAnonQueue`'s server-side dedupe/cap guard is the actual source of truth once merged.
 */

const STORAGE_KEY = 'narcis:anonPackQueue:v1'
const MERGED_FLAG_KEY = 'narcis:anonPackQueueMerged:v1'

/** Mirrors `PACK_QUEUE_CAP` (packQueue.server.ts) — kept as a literal, not imported, since that
 *  module is `.server.ts` and this file must stay client-safe. */
export const ANON_QUEUE_CAP = 3

function readRaw(): string[] {
    if (typeof localStorage === 'undefined') return []
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return []
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : []
    } catch {
        return []
    }
}

function writeRaw(slugs: string[]): void {
    if (typeof localStorage === 'undefined') return
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(slugs))
    } catch {
        // Storage full/unavailable — the queue simply doesn't persist; no crash.
    }
}

export function getAnonQueue(): string[] {
    return readRaw()
}

export function isAnonQueued(packSlug: string): boolean {
    return readRaw().includes(packSlug)
}

export type AnonQueueResult =
    | {status: 'queued'; position: number}
    | {status: 'already-queued'; position: number}
    | {status: 'cap-reached'}

export function addToAnonQueue(packSlug: string): AnonQueueResult {
    const current = readRaw()
    const existingIndex = current.indexOf(packSlug)
    if (existingIndex >= 0) return {status: 'already-queued', position: existingIndex + 1}
    if (current.length >= ANON_QUEUE_CAP) return {status: 'cap-reached'}
    const next = [...current, packSlug]
    writeRaw(next)
    return {status: 'queued', position: next.length}
}

export function removeFromAnonQueue(packSlug: string): {status: 'unqueued' | 'not-queued'} {
    const current = readRaw()
    if (!current.includes(packSlug)) return {status: 'not-queued'}
    writeRaw(current.filter((s) => s !== packSlug))
    return {status: 'unqueued'}
}

/** Whether this browser has already folded its anon queue into the DB queue once — guards
 *  `usePackQueue`'s merge effect against re-POSTing merge-anon on every authenticated page load. */
export function hasMergedAnonQueue(): boolean {
    if (typeof localStorage === 'undefined') return true
    try {
        return localStorage.getItem(MERGED_FLAG_KEY) === '1'
    } catch {
        return true
    }
}

/** Marks the merge as done and clears the anon list — called once the server confirms the
 *  merge-anon POST succeeded (whatever it reports: queued/already-queued/cap-reached are all
 *  terminal outcomes for the anon list itself). */
export function markAnonQueueMerged(): void {
    if (typeof localStorage === 'undefined') return
    try {
        localStorage.setItem(MERGED_FLAG_KEY, '1')
        localStorage.removeItem(STORAGE_KEY)
    } catch {
        // Best-effort — a failed clear just means the merge could fire again next load, which
        // merge-anon's own server-side dedupe already handles harmlessly.
    }
}
